import { useCallback, useEffect, useMemo } from "react";
import type { MutableRefObject } from "react";
import type { TreeApi } from "react-arborist";
import type { TreeNodeData } from "../types";
import {
  expandedKeysToOpenState,
  getExpandedKeysFromTree,
} from "../utils/adapter";

interface UseTreeExpansionOptions {
  expandedKeys?: string[];
  defaultExpandedKeys?: string[];
  autoExpandParent?: boolean;
  onExpand?: (
    keys: string[],
    info: { expanded: boolean; node: TreeNodeData }
  ) => void;
  treeRef: MutableRefObject<TreeApi<TreeNodeData> | undefined>;
}

export const useTreeExpansion = ({
  expandedKeys: controlledKeys,
  defaultExpandedKeys,
  autoExpandParent,
  onExpand,
  treeRef,
}: UseTreeExpansionOptions) => {
  const isControlled = controlledKeys !== undefined;

  const initialOpenState = useMemo(
    () => expandedKeysToOpenState(controlledKeys ?? defaultExpandedKeys ?? []),
    []
  );

  useEffect(() => {
    if (!isControlled || !treeRef.current) return;
    const tree = treeRef.current;
    const desired = new Set(controlledKeys);

    tree.root.children?.forEach(function syncNode(node) {
      if (node.isInternal) {
        if (desired.has(node.id) && !node.isOpen) node.open();
        if (!desired.has(node.id) && node.isOpen) node.close();
        node.children?.forEach(syncNode);
      }
    });
  }, [controlledKeys, isControlled, treeRef]);

  const handleToggle = useCallback(
    (id: string) => {
      if (!onExpand || !treeRef.current) return;
      const tree = treeRef.current;
      const node = tree.get(id);
      if (!node) return;

      if (autoExpandParent && node.isOpen) {
        node.openParents();
      }

      const keys = getExpandedKeysFromTree(
        tree.root.children as unknown as {
          id: string;
          isOpen: boolean;
          children: unknown[] | null;
        }[]
      );
      onExpand(keys, { expanded: node.isOpen, node: node.data });
    },
    [onExpand, autoExpandParent, treeRef]
  );

  return { initialOpenState, handleToggle };
};
